import BigNumber from 'bignumber.js';
import html2canvas from 'html2canvas';
import moment from 'moment';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCurrencyDollar } from 'react-icons/bs';
import QRCode from 'react-qr-code';
import { MView } from '../../../styles/mobilePageStyles';
import { Column, Row, Text } from '../../../styles/reactnative';
import { ThemeContext } from '../../../theme/ThemeProvider';
import { COLORS, FONTS } from '../../constants';
import { useFetcher, useFetcherPublic } from '../../hooks/fetcher';
import { useExchangeStore } from '../../hooks/trade/exchangeStore';
import { useIndexPrice } from '../../hooks/trade/indexprice';
import {
  BackSvg,
  CancelBtn,
  CustomModal,
  DownloadBtn,
  ModalDesContainer,
  ModalDesName,
  ModalDesValue,
  QRContainer,
} from './styles';
import { useSystemSetting } from '@/app/hooks/system/GetSystemSetting';
import { VITE_APP_API_MEDIA_URL } from '@/app/constants/APIKEYS';

function useImageDownload(backgroundColor: string, isDarkMode: boolean) {
  const captureArea = useRef<HTMLDivElement>(null);

  async function getLocalImageUrl(url: string) {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return URL.createObjectURL(blob); // blob://xxxx 형태
  }
  /* 다운로드 버튼 함수 */
  const onImageDownload = async (systemImage: string) => {
    if (!captureArea.current) return;

    if (systemImage) {
      const localUrl = await getLocalImageUrl(
        `${VITE_APP_API_MEDIA_URL}/${systemImage}`
      );

      const backgroundImgElement = new Image();
      backgroundImgElement.src = localUrl;

      backgroundImgElement.onload = async () => {
        captureArea.current!.style.backgroundImage = `url(${localUrl})`;
        captureArea.current!.style.backgroundSize = 'cover';
        captureArea.current!.style.backgroundPosition = 'center';
        captureArea.current!.style.backgroundRepeat = 'no-repeat';
        captureArea.current!.style.padding = '40px';
        captureArea.current!.style.backgroundColor = 'transparent';

        //안전하게 한 프레임 대기
        await new Promise((r) => requestAnimationFrame(r));

        const canvas = await html2canvas(captureArea.current!, {
          scale: 4,
          backgroundColor: backgroundColor,
          useCORS: true,
        });
        captureArea.current!.style.backgroundImage = '';
        captureArea.current!.style.padding = '0px';
        const element = document.createElement('a');
        element.href = canvas.toDataURL('image/png');
        element.download = 'SHARE.png';
        element.click();
      };
    } else {
      // div 컨테이너 생성
      const divContainer = document.createElement('div');
      divContainer.style.position = 'absolute';
      divContainer.style.right = '-80px';
      divContainer.style.bottom = '50px';
      divContainer.style.zIndex = '-1'; // 콘텐츠(z-index: 2) 뒤로 배치

      // SVG 엘리먼트 생성
      const svgElement = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      svgElement.setAttribute('viewBox', '0 0 54 45');
      svgElement.setAttribute('fill', 'none');
      svgElement.style.width = '371.7px';
      svgElement.style.height = '311.57px';

      // SVG 색상 설정
      const backSvgColor = isDarkMode ? '#252525' : '#F8F9FB';

      // SVG 내부 path 엘리먼트들 생성
      const paths = [
        'M38.7781 22.4986L16.5704 44.75L7.70655 44.7533L29.9143 22.5021L7.703 0.24707H16.5704L38.7781 22.4986Z',
        'M24.3723 22.6039L9.37282 37.7766L0.508949 37.78L15.5084 22.6072L0.5 7.42548H9.36742L24.3723 22.6039Z',
        'M32.8847 31.2169L46.3924 44.7533H37.599L28.4882 35.6229L32.8847 31.2169Z',
        'M28.4882 9.51622L37.5963 0.250398L46.3924 0.24707L32.8844 13.9886L28.4882 9.51622Z',
        'M35.4852 16.6946L44.6981 7.42901L53.5952 7.42548L39.932 21.167L35.4852 16.6946Z',
        'M39.9324 23.8333L44.0649 28.0516L53.5952 37.78H44.7008L35.4852 28.3729L39.9324 23.8333Z',
      ];

      paths.forEach((pathData) => {
        const path = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        );
        path.setAttribute('fill-rule', 'evenodd');
        path.setAttribute('clip-rule', 'evenodd');
        path.setAttribute('d', pathData);
        path.setAttribute('fill', backSvgColor);
        svgElement.appendChild(path);
      });

      // SVG를 div에 추가하고, div를 captureArea에 추가
      divContainer.appendChild(svgElement);
      captureArea.current.appendChild(divContainer);
      captureArea.current.style.padding = '40px';

      const canvas = await html2canvas(captureArea.current, {
        scale: 4,
        backgroundColor: backgroundColor,
      });
      captureArea.current.style.padding = '0px';
      captureArea.current.removeChild(divContainer);
      const element = document.createElement('a');
      element.href = canvas.toDataURL('image/png');
      element.download = 'SHARE.png';
      element.click();
    }
  };

  // 이미지 blob 형태로 가져오기, 이부분은 아직 미완성, 필요할때 완성할 예정
  const getImageBlob = async () => {
    if (captureArea.current) {
      const canvas = await html2canvas(captureArea.current);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        return url;
      });
    }
  };

  return {
    captureArea,
    onImageDownload,
    getImageBlob,
  };
}

const ShareModal = ({ shareData, setShareData, type = 1 }) => {
  const THEMECOLORS = useContext(ThemeContext).theme.COLORS;
  const { isDarkMode, theme } = useContext(ThemeContext);
  const { EPs } = useExchangeStore();
  const [lastprice, setLastPrice] = React.useState(0);

  const [showpnl, setShowpnl] = React.useState(true);
  const [showRate, setShowRate] = React.useState(true);
  const [showprice, setShowprice] = React.useState(true);
  const [showreferral, setShowreferral] = React.useState(true);
  const { t } = useTranslation();

  const { captureArea, onImageDownload, getImageBlob } = useImageDownload(
    theme.COLORS.PrimaryBg,
    isDarkMode
  );

  const { SystemSetting: backgroundImage } = useSystemSetting('CHIWEB'); // 시스템에 설정되어 있는 백그라운드 이미지지
  const systemImage =
    backgroundImage?.result === 'undefined' ? null : backgroundImage?.result;

  const { getPrice } = useIndexPrice(shareData?.exchange_pairs_id);
  const { data } = useFetcher('/referral/code', false);
  const currencyList = useFetcherPublic('/system/currency');

  const equivusd = currencyList?.data?.result?.result?.find(
    (item: any) => item?.code === 'KRW'
  );

  const backSvgColor = isDarkMode ? '#252525' : '#F8F9FB';

  useEffect(() => {
    if (!shareData) return;
    if (type == 1) {
      const price = getPrice(shareData?.exchange_pairs_id);
      setLastPrice(price);
      if (!price) {
        setTimeout(() => {
          setLastPrice(getPrice(shareData?.exchange_pairs_id));
        }, 1000);
      }
    } else {
      // type=2일 때는 종료된 포지션의 평균 진입가 계산
      const openOrders =
        shareData?.orders?.filter((order: any) => order.open_close === 'O') ||
        [];
      let avgEntryPrice = 0;

      if (openOrders.length > 0) {
        let totalAmount = 0;
        let weightedSum = 0;
        openOrders.forEach((order: any) => {
          const amount = Number(order.amount || 0);
          const price = Number(order.trade_price || 0);
          totalAmount += amount;
          weightedSum += amount * price;
        });
        avgEntryPrice = totalAmount > 0 ? weightedSum / totalAmount : 0;
      }

      setLastPrice(
        avgEntryPrice && !isNaN(Number(avgEntryPrice))
          ? Number(avgEntryPrice)
          : 0
      );
    }
    setShowpnl(true);
    setShowRate(true);
    setShowprice(true);
    setShowreferral(true);
  }, [shareData]);

  if (!shareData) {
    return null;
  }
  const referralCode = data?.result?.result?.code || '';

  const EP = EPs.find((ep) => ep.id === shareData?.exchange_pairs_id);

  let pnl = 0;
  let pnlRate = 0;

  if (type == 1) {
    // 현재 포지션의 PnL 계산 시 유효성 검증
    const entryPrice = shareData?.price; // 현재 포지션의 진입가
    const amount = shareData?.amount;
    const leverage = shareData?.leverage;
    const margin = shareData?.margin;

    if (
      entryPrice &&
      !isNaN(Number(entryPrice)) &&
      amount &&
      !isNaN(Number(amount)) &&
      leverage &&
      !isNaN(Number(leverage)) &&
      lastprice &&
      !isNaN(Number(lastprice))
    ) {
      if (shareData?.long_short == 'S') {
        pnl = BigNumber(BigNumber(entryPrice).minus(lastprice))
          .multipliedBy(amount)
          .multipliedBy(leverage)
          .toNumber();
      } else {
        pnl = BigNumber(BigNumber(lastprice).minus(entryPrice))
          .multipliedBy(amount)
          .multipliedBy(leverage)
          .toNumber();
      }

      if (margin && !isNaN(Number(margin)) && Number(margin) > 0) {
        pnlRate = BigNumber(pnl).dividedBy(margin).multipliedBy(100).toNumber();
      }
    }
  } else {
    // 종료된 포지션의 PnL 계산 시 유효성 검증
    const pnlPrice = shareData?.pnl_price;
    const margin = shareData?.margin;

    if (pnlPrice && !isNaN(Number(pnlPrice))) {
      pnl = Number(pnlPrice);

      if (margin && !isNaN(Number(margin)) && Number(margin) > 0) {
        pnlRate = new BigNumber(pnlPrice)
          .dividedBy(margin)
          .multipliedBy(100)
          .toNumber();
      }
    }
  }

  return (
    <CustomModal
      style={{ position: 'relative' }}
      open={shareData ? true : false}
      footer={null}
      onCancel={() => {
        setShareData(null);
      }}
      closable={false}
      systemImage={systemImage}
    >
      <Column
        style={{
          height: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          padding: '40px',
          overflow: 'hidden',
        }}
      >
        {!systemImage && (
          <div
            style={{
              position: 'absolute',
              right: '-80px',
              bottom: '130px',
              zIndex: 1,
            }}
          >
            <BackSvg
              width="54"
              height="45"
              viewBox="0 0 54 45"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M38.7781 22.4986L16.5704 44.75L7.70655 44.7533L29.9143 22.5021L7.703 0.24707H16.5704L38.7781 22.4986Z"
                fill={backSvgColor}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M24.3723 22.6039L9.37282 37.7766L0.508949 37.78L15.5084 22.6072L0.5 7.42548H9.36742L24.3723 22.6039Z"
                fill={backSvgColor}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M32.8847 31.2169L46.3924 44.7533H37.599L28.4882 35.6229L32.8847 31.2169Z"
                fill={backSvgColor}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M28.4882 9.51622L37.5963 0.250398L46.3924 0.24707L32.8844 13.9886L28.4882 9.51622Z"
                fill={backSvgColor}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M35.4852 16.6946L44.6981 7.42901L53.5952 7.42548L39.932 21.167L35.4852 16.6946Z"
                fill={backSvgColor}
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M39.9324 23.8333L44.0649 28.0516L53.5952 37.78H44.7008L35.4852 28.3729L39.9324 23.8333Z"
                fill={backSvgColor}
              />
            </BackSvg>
          </div>
        )}

        <Column ref={captureArea} style={{ position: 'relative', zIndex: 2 }}>
          {' '}
          <MView>
            <img
              alt="LOGO"
              src={
                isDarkMode
                  ? '/images/Qbit/logo-dark.png'
                  : '/images/Qbit/logo-light.png'
              }
              style={{ height: '28px', width: '134px' }}
            />
          </MView>
          <MView style={{ width: '235px', height: '128px', marginTop: '34px' }}>
            <MView
              style={{
                flexDirection: 'row',
                width: '235px',
                // width: '300px',
                alignItems: 'center',
                marginTop: '0px',
                height: '25px',
                gap: '12px',
                display: 'flex',
                // backgroundColor: 'blue',
              }}
            >
              <div
                style={{
                  fontWeight: '500',
                  color: THEMECOLORS.textw,
                  fontSize: '20px',
                  fontFamily: 'pretendard',
                  lineHeight: '100%',
                }}
              >
                {EP?.pairs}
              </div>
              <span style={{ height: '18px', color: '#474747' }}>ㅣ</span>
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color:
                    shareData?.long_short == 'S'
                      ? COLORS.lightRed
                      : COLORS.lightGreen,
                  backgroundColor:
                    shareData?.long_short == 'S'
                      ? 'rgba(246, 71, 93, 0.1)'
                      : 'rgba(13, 203, 129, 0.1)',
                  borderRadius: '2px',
                  padding: '2px 6px',
                }}
              >
                {shareData?.long_short == 'S' ? 'short' : 'Long'}
              </span>
              <span style={{ height: '18px', color: '#474747' }}>ㅣ</span>
              <Text
                style={{
                  ...FONTS.h3,
                  color: THEMECOLORS.textw,
                  fontSize: '20px',
                  fontFamily: 'pretendard',
                  lineHeight: '100%',
                  fontWeight: '500',
                }}
              >
                {shareData?.leverage}x
              </Text>
            </MView>

            <MView style={{ marginTop: '20px', gap: '0px' }}>
              {showpnl && (
                <MView
                  style={{
                    flexDirection: 'row',
                    width: '100%',

                    // backgroundColor:'white',
                    marginBottom: '0px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '36px',
                      fontFamily: 'pretendard',
                      fontWeight: '500',
                      lineHeight: '100%',
                      whiteSpace: 'nowrap',
                      color:
                        pnl > 0
                          ? COLORS.lightGreen
                          : pnl < 0
                            ? COLORS.lightRed
                            : 'white',
                    }}
                  >
                    {/* {pnl > 0 ? "+" : ""} */}
                    {new BigNumber(pnl).toFormat(
                      EP?.dp || 4
                    )} USDT
                  </div>
                </MView>
              )}

              {showRate && (
                <Text
                  size={24}
                  style={{
                    color:
                      pnl > 0
                        ? COLORS.lightGreen
                        : pnl < 0
                          ? COLORS.lightRed
                          : 'white',
                    fontWeight: '600',
                    fontFamily: 'pretendard',
                    fontSize: '24px',
                  }}
                >
                  {pnl > 0 ? '+ ' : ''}
                  {pnlRate.toFixed(2)}
                  <span
                    style={{
                      fontWeight: '500',
                      fontSize: '24px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                    }}
                  >
                    %
                  </span>
                </Text>
              )}
            </MView>
          </MView>
          <ModalDesContainer>
            {showprice && (
              <Row style={{ gap: `68px` }}>
                <Column style={{ gap: '6px' }}>
                  <ModalDesName isDarkmode={isDarkMode}>
                    {t('entryPrice')}
                  </ModalDesName>
                  <ModalDesValue>
                    <BsCurrencyDollar />
                    {(() => {
                      if (type == 1) {
                        // 현재 포지션: shareData?.price (진입가)
                        const price = shareData?.price;
                        return price && !isNaN(Number(price))
                          ? new BigNumber(price).toFormat(EP?.dp || 3)
                          : '0.000';
                      } else {
                        // 종료된 포지션: 오픈 주문들의 가중평균 진입가 표시 (lastprice)
                        return lastprice && !isNaN(Number(lastprice))
                          ? new BigNumber(lastprice).toFormat(EP?.dp || 3)
                          : '0.000';
                      }
                    })()}
                  </ModalDesValue>
                </Column>

                <Column style={{ gap: '6px' }}>
                  <ModalDesName isDarkmode={isDarkMode}>
                    {t('markPrice')}
                  </ModalDesName>
                  <ModalDesValue>
                    <BsCurrencyDollar />
                    {(() => {
                      if (type == 1) {
                        // 현재 포지션: 공정가격 (lastprice)
                        return lastprice && !isNaN(Number(lastprice))
                          ? new BigNumber(lastprice).toFormat(EP?.dp || 3)
                          : '0.000';
                      } else {
                        // 종료된 포지션: 클로즈 주문들의 가중평균 청산가 계산
                        const closeOrders =
                          shareData?.orders?.filter(
                            (order: any) => order.open_close === 'C'
                          ) || [];
                        let avgExitPrice = 0;

                        if (closeOrders.length > 0) {
                          let totalAmount = 0;
                          let weightedSum = 0;
                          closeOrders.forEach((order: any) => {
                            const amount = Number(order.amount || 0);
                            const price = Number(order.trade_price || 0);
                            totalAmount += amount;
                            weightedSum += amount * price;
                          });
                          avgExitPrice =
                            totalAmount > 0 ? weightedSum / totalAmount : 0;
                        }

                        return avgExitPrice && !isNaN(Number(avgExitPrice))
                          ? new BigNumber(avgExitPrice).toFormat(EP?.dp || 3)
                          : '0.000';
                      }
                    })()}
                  </ModalDesValue>
                </Column>
              </Row>
            )}

            <Column style={{ gap: '6px' }}>
              <ModalDesName isDarkmode={isDarkMode}>
                {t(type == 1 ? 'entryTime' : 'endDate')}
              </ModalDesName>
              <ModalDesValue>
                {moment
                  .utc(
                    type == 1
                      ? shareData?.created_at
                      : shareData?.closed_at ||
                          shareData?.updated_at ||
                          shareData?.created_at
                  )
                  .utcOffset(9)
                  .format('YYYY-MM-DD HH:mm:ss')}
              </ModalDesValue>
            </Column>

            <Column style={{ gap: '6px' }}>
              <ModalDesName isDarkmode={isDarkMode}>
                {t('referralCode')}
              </ModalDesName>
              <Row style={{ gap: '12px', alignItems: 'center' }}>
                <QRContainer>
                  <QRCode
                    size={256}
                    style={{
                      height: 'auto',
                      maxWidth: 80,
                      width: 80,
                    }}
                    value={`https://quanturmbit.com/signup?ref=${referralCode}`}
                    viewBox={`0 0 80 80`}
                  />
                </QRContainer>
                {referralCode}
              </Row>
            </Column>
          </ModalDesContainer>
        </Column>

        <Column
          style={{
            gap: '8px',
            paddingTop: '20px',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Row
            style={{ justifyContent: 'center', gap: '10px', height: '40px' }}
          >
            <DownloadBtn
              onClick={() => {
                onImageDownload(systemImage);
              }}
            >
              {t('imagedownload')}
            </DownloadBtn>
            <CancelBtn onClick={() => setShareData(null)}>
              {t('CLOSE')}
            </CancelBtn>
          </Row>
          <Row style={{ justifyContent: 'center', gap: '16px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: THEMECOLORS.textw,
              }}
            >
              <input
                type="checkbox"
                checked={showpnl}
                onChange={(e) => setShowpnl(e.target.checked)}
              />
              {t('Profit')}
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: THEMECOLORS.textw,
              }}
            >
              <input
                type="checkbox"
                checked={showRate}
                onChange={(e) => setShowRate(e.target.checked)}
              />
              {t('ROI')}
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: THEMECOLORS.textw,
              }}
            >
              <input
                type="checkbox"
                checked={showprice}
                onChange={(e) => setShowprice(e.target.checked)}
              />
              {t('price')}
            </label>
          </Row>
        </Column>
      </Column>
    </CustomModal>
  );
};
export default React.memo(ShareModal);
